class MediaPreviewPanel extends BaseInfoPanel {

	/**
	 *
	 */
	public collection: CollectionData;

	/**
	 * All the contents of the media preview
	 */
	public mediaPreviewEl: HTMLElement;

	/**
	 * Element that contains backwards, forwards, and view buttons
	 * @private
	 */
	private buttonWrap: HTMLElement;

	/**
	 * Link to view record
	 * @private
	 */
	private viewLink: HTMLAnchorElement;

	/**
	 * Button to move to next item
	 * @private
	 */
	private forwardArrow: HTMLElement;

	/**
	 * Button to move to previous item
	 * @private
	 */
	private backArrow: HTMLElement;

	/**
	 * Current record being previewed in the media preview panel
	 */
	public recordPreview: any;

	/**
	 * Current position of the records in the list to navigate via the media preview panel
	 */
	public recordPreviewIndex: number = 0;

	/**
	 * bound event so it can be removed
	 * @private
	 */
	private setActiveListener: () => void;

	/**
	 * Our keyboard listener callback
	 * @private
	 */
	private keyboardListener: () => void;

	private collectionSortListener: () => void;

	/**
	 *
	 * @param collection
	 */
	constructor(collection: CollectionData) {
		super();
		this.collection = collection;
		this.view();
		this.setupEvents();
	}

	/**
	 *
	 * @protected
	 */
	protected view() {
		this.frag = document.createDocumentFragment();
		this.recordPreview = this.collection.items[this.recordPreviewIndex];
		this.mediaPreviewEl = iwUI.createEl('div', {class: 'iPanel-content iPanel-content--preview'});
		this.forwardArrow = iwUI.createEl('button', {class: 'iw-icon-forward preview-btn iw_tool_tip_link', 'data-tool-tip': iwi18n.gettext(undefined, 'Next'), 'aria-label': iwi18n.gettext(undefined, 'Next')});
		this.backArrow = iwUI.createEl('button', {class: 'iw-icon-back preview-btn is-disabled iw_tool_tip_link', 'data-tool-tip': iwi18n.gettext(undefined, 'Previous'), 'aria-label': iwi18n.gettext(undefined, 'Previous')});
		this.buttonWrap = iwUI.createEl('DIV', {class: 'preview-actions'});
		this.viewLink = iwUI.createEl('a', {class: 'preview-btn icon-view iw-icon-left-arrow iw_tool_tip_link', 'data-tool-tip': iwi18n.gettext(undefined, 'View record'), 'aria-label':iwi18n.gettext(undefined, 'View record')});

		let viewIcon = iwUI.createEl('i');

		this.viewLink.appendChild(viewIcon);

		// cant cycle with 1 item
		if (this.collection.items.length > 1) {
			this.buttonWrap.append(this.backArrow, this.forwardArrow);
		}

		this.buttonWrap.append(this.viewLink);
		this.frag.appendChild(this.mainContainer);

		if (this.collection.items.length) {
			this.loadPreview();
		}

		// wait until the items are on the page to grab and highlight the collection item
		window.requestAnimationFrame(() => this.setActive(this.recordPreviewIndex));
	}

	/**
	 * Set record preview index to a specific index
	 * @param e
	 * @private
	 */
	private setActiveInfoEvent(e: { detail: { position: number } }) {
		// we are finding the position here instead of just grabbing the item at that index because there could be an item at that
		// position that the user is not able to view
		let index = this.collection.items.findIndex((f: any) => f.position === e.detail.position);

		if (index !== this.recordPreviewIndex) {
			this.recordPreviewIndex = index;
			this.updateNavButtons(this.recordPreviewIndex, this.collection.items.length);
			this.setActiveIndex();
		}
	}

	/**
	 * load the current index
	 * @private
	 */
	private setActiveIndex() {
		this.recordPreview = this.collection.items[this.recordPreviewIndex] ?? null;
		this.loadPreview();
		this.setActive(this.recordPreviewIndex);
	}

	/**
	 * Load Preview
	 */
	public loadPreview() {
		this.requests.forEach((f: XMLHttpRequest) => f.abort());
		this.requests = [];
		iwCore.clearElement(this.mediaPreviewEl);
		iwCore.clearElement(this.mainContainer);

		if (this.mediaContainer) {
			iwCore.clearElement(this.mediaContainer);
		}

		this.mainContainer.className = 'iPanel-preview';

		let mediaType = this.recordPreview.Fields.mediaType.toLowerCase(),
			clipClass = this.recordPreview.Type === 'clip' ? ' isClip' : '',
			mType = iwUI.createEl('DIV', {class: 'preview-type media-type iw_tool_tip_link' + ' ' + ('media-type--' + this.recordPreview.Fields.mediaType),
				'data-tool-tip': iwi18n.gettext(undefined, 'Media type is %1', this.recordPreview.Fields.mediaType)}),
			summary = iwUI.createEl('p', {class: 'preview-summary'}),
			fieldContents = iwUI.createEl('dl', {class: 'preview-fieldContents'}),
			mSummaryWrap = iwUI.createEl('div', {class: 'preview-summaryWrap'}),
			title = iwUI.createEl('h2', {class: 'preview-title'}),
			titleTxt =  iwUI.createEl('SPAN', {class: 'preview-title-txt iw_tool_tip_link', 'data-tool-tip': iwi18n.gettext(undefined, 'View record')}),
			titleBtn = iwUI.createEl('BUTTON', {class: 'icon-view iw-icon-left-arrow iw_tool_tip_link', 'data-tool-tip': iwi18n.gettext(undefined, 'View record')}),
			cDate = iwUI.createEl('p', {class: 'preview-date'}),
			imgWrap = iwUI.createEl('div', {class: 'preview-mediaWrap preview-mediaWrap--' + mediaType}),
			mInfoWrap = iwUI.createEl('div',{class: 'preview-mInfoWrap' +  (clipClass)}),
			mInfo = iwUI.createEl('div', {class: 'preview-mInfo'}),
			footer = iwUI.createEl('DIV', {class: 'preview__mInfo-footer'}),
			fakeLink = iwUI.createEl('a', {href: this.recordPreview.recordLink});

		imgWrap.appendChild(this.createMediaContainer(this.sortParams()));

		titleTxt.textContent = this.recordPreview.fieldClasses.title;
		cDate.textContent = this.recordPreview.fieldClasses.date;
		mType.setAttribute('aria-label', iwi18n.gettext(undefined, 'Media type is %1', this.recordPreview.Fields.mediaType));

		titleBtn.setAttribute('aria-label', iwi18n.gettext(undefined, 'View record'));
		summary.textContent = this.recordPreview.fieldClasses.summary ?? '';

		this.viewLink.href = this.recordPreview.recordLink;

		EVENT.attach(titleTxt, 'click', () => fakeLink.click())
		EVENT.attach(titleBtn, 'click', () => fakeLink.click())

		mSummaryWrap.append(fieldContents);
		footer.append(cDate, mType);
		title.append(titleTxt, titleBtn);
		mInfo.append(title, summary, footer);
		mInfoWrap.append(mInfo);
		imgWrap.append(mInfoWrap);

		this.mediaPreviewEl.appendChild(mSummaryWrap);
		this.mainContainer.appendChild(imgWrap);
		this.mainContainer.appendChild(this.buttonWrap);
		this.mainContainer.appendChild(this.mediaPreviewEl);

		iwToolTips.reInit();

		this.requests.push(iwCore.ajax({
			type: 'GET',
			url: '/record/retrieve/',
			data: {
				id: this.recordPreview.recordID,
				collectionsTemplate: true
			},
			beforeSend: () => iwCore.loading(true),
			complete: () => iwCore.loading(false),
			success: (e: any) => {
				// in case the user is rapidly clicking next, this will prevent old content being appended to new content
				iwCore.clearElement(fieldContents);
				fieldContents.innerHTML = e.template;

			},
			error: (e: any) => {
				// if the request hasn't started yet or its done, it may be erroring because we aborted it
				if (e.readyState !== 0 && e.readyState !== 4) {
					iwNotifications.error(iwi18n.gettext(undefined, 'Something went wrong, please try again later'))
				}
			}
		}));
	}

	/**
	 *
	 * @private
	 */
	private sortParams(): ResultObject {
		// we are using mergeObject here because we want to keep the original object intact
		// But want to make unnecessary and use same object everwhere
		return iwCore.mergeObject(this.recordPreview, {
				mediaType: this.recordPreview.Fields.mediaType,
				refImageUrl: this.recordPreview.Fields.thumbnail,
				url: this.recordPreview.recordLink});
	}

	/**
	 * Set active item on preview panel
	 */
	public setActive(index: number) {
		let items = document.getElementsByClassName('searchHit-item');

		iwCore.massClass(iwCore.removeClassName, items, 'is-active');
		iwCore.addClassName(items[index], 'is-active');

		if (items[index]) {
			if (iwCore.browser.mobile) {
				// subtract 5 so it when we are getting close to the end it goes to load more before we get there
				if (this.recordPreviewIndex >= (this.collection.items.length - 5)) {
					EVENT.custom('InfoPanel', {loadMore: true});
				}
			} else {
				items[index].scrollIntoView({block: 'nearest', inline: 'nearest', behavior: 'smooth'});
			}
		}
	}

	/**
	 * Ensure we loop through the current selection when we reach the ends
	 * @param navigatingForward
	 * @private
	 */
	private getNextIndex(navigatingForward: number): boolean {
		let currentSize = this.collection.items.length - 1, // we are using current size because the code will load more as the user goes along
			nextIndex =  navigatingForward ? this.recordPreviewIndex + 1 : this.recordPreviewIndex - 1;

		this.updateNavButtons(nextIndex, currentSize);

		// we cant allow going from the end to the beginning or the beginning back to the end because of the issue with numhits being incorrect
		// if the current user isn't allowed to see some of the media in the collection
		if (((this.recordPreviewIndex === currentSize && navigatingForward) || (this.recordPreviewIndex === 0 && !navigatingForward))) {
			return false;
		}

		this.recordPreviewIndex = nextIndex;
		return true;
	}

	/**
	 * Enable / disable our buttons depending on if we are at the beginning/end of the list
	 * @param nextIndex
	 * @param currentSize
	 * @private
	 */
	private updateNavButtons(nextIndex: number, currentSize: number) {
		if ((nextIndex >= currentSize)) {
			iwCore.addClassName(this.forwardArrow, 'is-disabled');
		} else if (nextIndex <= 0) {
			iwCore.addClassName(this.backArrow, 'is-disabled');
		} else {
			iwCore.massClass(iwCore.removeClassName, [this.forwardArrow, this.backArrow], 'is-disabled');
		}
	}

	/**
	 *
	 * @param e
	 * @private
	 */
	private cycleKeyboard(e: KeyboardEvent) {
		let canCycle = false;

		if (e.key === 'ArrowRight') {
			canCycle = this.getNextIndex(1);
		} else if (e.key === 'ArrowLeft') {
			canCycle = this.getNextIndex(0);
		}

		if (canCycle) {
			this.setActiveIndex();
		}
	}

	/**
	 * Setup Event Listeners
	 *
	 * @private
	 */
	private setupEvents() {


		EVENT.attach(this.forwardArrow, 'click', () => {
			if (this.getNextIndex(1)) {
				this.setActiveIndex();
			}
		});

		EVENT.attach(this.backArrow, 'click', () => {
			if (this.getNextIndex(0)) {
				this.setActiveIndex();
			}
		});

		this.collectionSortListener = this.collectionSortInfoEvent.bind(this);
		EVENT.attach(document, 'collectionSorted', this.collectionSortListener);

		this.keyboardListener = this.cycleKeyboard.bind(this);
		EVENT.attach(document, 'keyup', this.keyboardListener);

		this.setActiveListener = this.setActiveInfoEvent.bind(this);
		EVENT.attach(document, 'SetActiveInfo', this.setActiveListener);
	}

	private collectionSortInfoEvent(e: any) {
		this.setActiveInfoEvent({detail: { position: e.detail.newIndex } });
	}

	/**
	 * Update the panel
	 */
	public update() {
		if (this.recordPreviewIndex === null) {
			this.recordPreviewIndex = 0;
			this.setActiveIndex();
		}
	}

	/**
	 *
	 */
	public close() {
		EVENT.remove(document, 'SetActiveInfo', this.setActiveListener);
		EVENT.remove(document, 'keyup', this.keyboardListener);
		EVENT.remove(document, 'collectionSorted', this.collectionSortListener);

		this.requests.forEach((f: XMLHttpRequest) => f.abort());
	}
}